import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';

import PortalDropdown, { DropdownMenuItem } from 'views_shared/app/javascript/components/PortalDropdown';

import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import { profileUrl } from 'views_shared/app/javascript/utils/urls';
import { qformat, timeZoneAbbr } from 'views_shared/app/javascript/utils/dateFormatters';
import { normalizeProfileType, getProfileTypeBadge } from 'views_shared/app/javascript/utils/profileTypeHelpers';
import { isPitchRead } from 'views_shared/app/javascript/utils/pitchStateHelpers';
import EntitySubtitle from 'views_shared/app/javascript/components/EntitySubtitle';
import AiDetectorButton from '../../pitch/pangram/ai_detector_button';
import { REQUEST_VIDEO_MESSAGE } from '../../my_inbox/videoRequest';
import Attachment from '../../my_inbox/Attachment';
import MessageContent from '../../my_inbox/Message/MessageContent';
import stripHtmlTags from '../../my_inbox/ConversationsList/Conversation/stripHtmlTags';
import { htmlToMultilineText } from './buildPitchesText';
import FirstSendComposer from '../../my_inbox/FirstSendComposer';
import PitchMuteModal from './PitchMuteModal';
import PitchReportModal from './PitchReportModal';
import vapi from '../../../javascript/frontend/api/vapi';
import vahoy from '../../../javascript/vahoy';
import stripHtmlToVisibleText from '../../../javascript/stripHtmlToVisibleText';

// Line-clamp drops content after a hard line break from layout entirely, so
// scrollHeight matches clientHeight even with paragraphs hidden ("Hi Andi,\n\n…" showed
// no Expand). Measure an invisible unclamped clone instead — still measured, not guessed.
const clampHides = (node) => {
  const clone = node.cloneNode(true);
  // Set properties individually — replacing cssText would wipe the node's inline
  // white-space, collapsing the line breaks we are trying to measure.
  clone.style.position = 'absolute';
  clone.style.visibility = 'hidden';
  clone.style.display = 'block';
  clone.style.overflow = 'visible';
  clone.style.height = 'auto';
  clone.style.webkitLineClamp = 'unset';
  clone.style.width = `${node.clientWidth}px`;
  node.parentElement.appendChild(clone);
  const hides = clone.offsetHeight > node.clientHeight + 1;
  clone.remove();
  return hides;
};

// V1's Pitch::FAST_PITCH_DURATION — a pitch this soon after the request may be AI-written
const FAST_PITCH_MS = 15 * 60 * 1000;

// V1's canned responses (reporter_followup.jsx) with the Figma strip's labels
const QUICK_REPLIES = [
  { label: "Let's talk!", body: "Thanks for your pitch! Let's talk more." },
  {
    label: "Sorry, I can't use this...",
    body:
      "Thanks for pitching, but I won't be able to use it this time around. "
      + "Please, don't let this discourage you from responding to future requests. :)",
  },
  {
    label: 'Schedule Interview',
    body:
      "Your pitch piqued my interest! I'd love to explore its potential and learn more about "
      + "your story and expertise through a video interview. Please let me know when you're "
      + "available, and I'll set up a call.",
  },
  { label: 'Request a video', body: REQUEST_VIDEO_MESSAGE, videoRequest: true },
];

// Table View pitch card (per the "New Pitch Inbox" Figma designs). Messaged cards show the
// exchanged banner (V1 parity: reply lives in the conversation); unmessaged cards get a
// Reply button opening the real composer in a modal — no view switching.
function PitchCard({
  pitch, conversation, request, location = null, selected = false, onToggleSelect = null, muteReport = null,
  canReply = true, onReplied, onReact = null, onViewConversation = null,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewOverflows, setPreviewOverflows] = useState(false);
  const [sendSlot, setSendSlot] = useState(null);
  const [showMute, setShowMute] = useState(false);
  // muted/reported live on the pitch prop — PitchInboxSection owns them so both
  // tabs and every menu for the same user stay in sync
  const muted = !!pitch.muted;
  const [muteDuration, setMuteDuration] = useState(`${muteReport?.defaultMuteDurationDays || 30}`);
  const [showReportAbuse, setShowReportAbuse] = useState(false);
  const reported = !!pitch.reported;
  const [reportDescription, setReportDescription] = useState('');
  const [reportBehaviorTypes, setReportBehaviorTypes] = useState();
  // Audit-flagged pitcher: V1 collapses the whole card behind the warning banner
  const [auditExpanded, setAuditExpanded] = useState(false);
  const isAudit = !!pitch.under_audit;

  // Measured on modal mount: the clamp only earns an Expand button when text actually overflows
  const previewRef = useCallback((node) => {
    if (node) setPreviewOverflows(clampHides(node));
  }, []);

  const pitcherFirstName = (pitch.profile_name || '').split(' ')[0] || 'user';
  const normalizedProfileType = normalizeProfileType(pitch.type_of_profile);
  const badge = getProfileTypeBadge(normalizedProfileType);
  const unread = !isPitchRead(pitch);
  const plainText = useMemo(() => stripHtmlTags(pitch.pitch_text || ''), [pitch.pitch_text]);
  // Full text for the modal preview — stripHtmlTags is the row-snippet helper (first line + …)
  const previewText = useMemo(() => htmlToMultilineText(pitch.pitch_text || ''), [pitch.pitch_text]);
  const [textOverflows, setTextOverflows] = useState(false);

  // Measured, not guessed: ...more only appears when the clamp actually hides text
  const clampRef = useCallback((node) => {
    if (node) setTextOverflows(clampHides(node));
  }, []);

  // A text-less pitch (video recorded on Qwoted) can't be AI-written, so it is never "fast".
  const isFastPitch = !!(pitch.sent_at && request.created_at)
    && plainText.trim().length > 0
    && new Date(pitch.sent_at) - new Date(request.created_at) < FAST_PITCH_MS;

  const fileAttachments = (pitch.pitch_attachments || []).filter(
    (a) => a.type !== 'Interact::Attachment::ReferencedModel',
  );

  // Same shape the thread pane feeds the composer/sidebar with
  const conversationShape = useMemo(
    () => ({
      conversation_id: `pitch-${pitch.id}`,
      conversation_senders: [
        {
          slug: pitch.profile_slug,
          name: pitch.profile_name,
          first_name: (pitch.profile_name || '').split(' ')[0],
          profile_image_url: pitch.profile_image_url,
          title_at_workplace: pitch.profile_title_at_workplace,
          type_of_profile: pitch.type_of_profile,
        },
      ],
      conversation_receivers: [],
    }),
    [pitch],
  );

  // Same flows as V1's MuteButton / ReportButton, triggered from the card's actions menu
  const submitMute = () => {
    vapi.muteUser({
      duration_days: muteDuration,
      muted_user_id: pitch.user_migrate_id,
      reporting_user_id: muteReport.reportingUserId,
    }).then(() => {
      setShowMute(false);
      muteReport.onMutedChange?.(pitch.user_migrate_id, true);
      globalThis.Qwoted.showToast?.({
        toastTitle: `${pitcherFirstName} muted`,
        toastBody: 'New pitches from them will be hidden. They can still reply within existing conversations.',
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to mute the user. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('MuteModal#muteUser');
  };

  const submitUnmute = () => {
    vapi.unmuteUser({
      muted_user_id: pitch.user_migrate_id,
      reporting_user_id: muteReport.reportingUserId,
    }).then(() => {
      muteReport.onMutedChange?.(pitch.user_migrate_id, false);
      globalThis.Qwoted.showToast?.({
        toastTitle: `${pitcherFirstName} unmuted`,
        toastBody: "You'll receive their new pitches again.",
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to unmute the user. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('PitchCard#unmuteUser');
  };

  const toggleBehaviorType = (key) => {
    setReportBehaviorTypes((prev) => {
      const next = [...(prev || [])];
      const index = next.indexOf(key);
      if (index >= 0) {
        next.splice(index, 1);
      } else {
        next.push(key);
      }
      return next;
    });
  };

  const submitReportAbuse = () => {
    vapi.reportAbuse({
      report_description: reportDescription,
      reported_behavior_types: reportBehaviorTypes || [],
      reported_id: pitch.id,
      reported_text: stripHtmlToVisibleText(pitch.pitch_text || ''),
      reported_type: 'Pitch',
      reported_url: window.location.href,
      reported_user_id: pitch.user_migrate_id,
      reporting_user_id: muteReport.reportingUserId,
    }).then(() => {
      setShowReportAbuse(false);
      muteReport.onReported?.(pitch.id);
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Report submitted',
        toastBody: 'Thanks for flagging this — our team will review it.',
        variant: 'success',
      });
    }).catch((error) => {
      globalThis.Qwoted.showToast?.({
        toastTitle: 'Error',
        toastBody: 'Failed to submit the report. Please try again.',
        variant: 'danger',
      });
      throw error;
    });
    vahoy.track('ReportModal#reportAbuse');
  };

  const handleSent = () => {
    setShowReply(false);
    globalThis.Qwoted.showToast?.({
      toastTitle: 'Reply sent',
      toastBody: `Your conversation with ${pitch.profile_name} is now in your inbox.`,
      variant: 'success',
    });
    onReplied();
  };

  const openReply = (reply = null) => {
    setPrefill(reply ? { body: `<p>${reply.body}</p>`, videoRequest: !!reply.videoRequest } : null);
    setShowReply(true);
  };

  return (
    <div className={`pi-pitch-card my-inbox ${isAudit ? 'pi-audit-card' : ''}`}>
      {isAudit && (
        <div
          className={'pi-audit-banner background-color-raspberry-80 text-white py-1 px-2 '
            + 'd-flex align-items-center justify-content-between'}
        >
          <span className="text-start">
            <i className="fa-solid fa-circle-exclamation text-white" aria-hidden="true" /> This user has been flagged
            by other media users &mdash; review pitch with caution.
          </span>
          <button
            type="button"
            className="text-white text-decoration-none small ms-3 flex-shrink-0 bg-transparent border-0"
            aria-expanded={auditExpanded}
            onClick={() => setAuditExpanded((e) => !e)}
          >
            {auditExpanded ? 'Hide Pitch' : 'View Pitch'}{' '}
            <i className="fa-solid fa-chevron-right ms-1" aria-hidden="true" />
          </button>
        </div>
      )}
      {(!isAudit || auditExpanded) && (
        <>
          <div className="pi-pitch-card-header">
        {onToggleSelect && (
          <input
            type="checkbox"
            className="form-check-input pi-select-check"
            checked={selected}
            onChange={() => onToggleSelect(pitch.id)}
            aria-label={`Select pitch from ${pitch.profile_name}`}
          />
        )}
        <a
          href={profileUrl({ slug: pitch.profile_slug, user_migrate_id: pitch.user_migrate_id })}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`View ${pitch.profile_name}'s profile`}
        >
          <ImageCropped
            roundedCircle
            src={pitch.profile_image_url}
            alt={pitch.profile_name}
            size={48}
            fallbackToInitials
          />
        </a>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="text-truncate">
            <a
              href={profileUrl({ slug: pitch.profile_slug, user_migrate_id: pitch.user_migrate_id })}
              target="_blank"
              rel="noopener noreferrer"
              className="pi-card-name pi-profile-link"
            >
              {pitch.profile_name}
            </a>
          </div>
          {pitch.profile_workplace && (
            <p className="paragraph-sm text-secondary mb-0 text-truncate">{pitch.profile_workplace}</p>
          )}
          {normalizedProfileType && (
            <span className={`pi-type-badge ${normalizedProfileType.replace(/_/g, '-')}`}>{badge.label}</span>
          )}
          {muted && (
            <span
              className="pi-type-badge pi-muted-badge ms-1"
              title="You muted this user — new pitches from them are hidden"
            >
              <i className="fa-solid fa-volume-xmark me-1" />
              Muted
            </span>
          )}
          {reported && (
            <span
              className="pi-type-badge pi-reported-badge ms-1"
              title="You reported this pitch — our team will review it"
            >
              <i className="fa-regular fa-flag me-1" />
              Reported
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {/* Like/dislike: the owner's private triage mark; a like is what the Liked Pitches tab lists.
              Clicking the pressed thumb clears it. Inert (no handler) for non-owner viewers. */}
          {['dislike', 'like'].map((reaction) => {
            const pressed = pitch.reaction === reaction;
            return (
              <button
                key={reaction}
                type="button"
                className={`btn btn-secondary btn-icon pi-reaction pi-reaction-${reaction} ${pressed ? 'active' : ''}`}
                aria-pressed={pressed}
                aria-label={reaction === 'like' ? 'Like' : 'Dislike'}
                disabled={!onReact}
                onClick={() => onReact?.(pitch.id, pressed ? null : reaction)}
              >
                <i
                  className={`${pressed ? 'fa-solid' : 'fa-regular'} fa-thumbs-${reaction === 'like' ? 'up' : 'down'}`}
                />
              </button>
            );
          })}
          {/* Reply always shows: first exchange opens the modal; an existing conversation
              opens the thread in a new tab, like the View Conversation link (design feedback) */}
          {canReply && (
            <button
              type="button"
              className={`btn ${conversation ? 'btn-secondary' : 'btn-primary'}`}
              onClick={() => (conversation
                ? window.open(`/my_inbox_v2?conversation=${conversation.conversation_id}`, '_blank', 'noopener')
                : openReply())}
            >
              <i className="fa-solid fa-message me-1" /> Reply
            </button>
          )}
          {/* Same PortalDropdown machinery as every other inbox menu; the bare-ellipsis
              look (Figma: the thumbs stay boxed, this doesn't) rides in via toggleClassName */}
          <PortalDropdown
            toggleAriaLabel="More actions"
            toggleClassName="btn btn-icon pi-actions-toggle"
          >
            {muted ? (
              <DropdownMenuItem
                icon="fa-solid fa-volume-high"
                label={`Unmute ${pitcherFirstName}`}
                disabled={!muteReport}
                onClick={submitUnmute}
              />
            ) : (
              <DropdownMenuItem
                icon="fa-solid fa-volume-xmark"
                label={`Mute ${pitcherFirstName}`}
                disabled={!muteReport}
                onClick={() => setShowMute(true)}
              />
            )}
            <DropdownMenuItem
              icon={`fa-${reported ? 'solid' : 'regular'} fa-flag`}
              label={reported ? 'Reported' : 'Report pitch'}
              disabled={!muteReport || reported}
              className="text-destructive-80"
              onClick={() => setShowReportAbuse(true)}
            />
          </PortalDropdown>
        </div>
      </div>

      {pitch.spam_flagged && (
        <div className="alert alert-danger small pi-fast-alert">
          <h4 className="alert-heading">
            <i className="fa-solid fa-circle-exclamation" /> Flagged User
          </h4>
          <p className="mb-0">
            User previously flagged by other Media users for SPAM and/or using AI in pitches. Proceed with caution:
            consider checking this pitch with a tool like{' '}
            <a href="https://pangram.com/" target="_blank" rel="noreferrer">Pangram</a>.
          </p>
        </div>
      )}

      {isFastPitch && (
        <div className="alert alert-warning small pi-fast-alert">
          <h4 className="alert-heading">
            <i className="fa-solid fa-circle-exclamation" /> Wow, that was fast!
          </h4>
          <p className="mb-2">
            This pitch came within 15 minutes of your request. Sometimes, a super-fast turnaround like this might mean
            AI was involved. To double-check, we recommend using
          </p>
          <p className="mb-0 d-flex align-items-center gap-1">
            <AiDetectorButton text={plainText} pitchId={pitch.id} displayAsButton={false} />, powered by Pangram!
          </p>
        </div>
      )}

      {/* Above the pitch text per design feedback (was below the card body) */}
      {conversation && conversation.num_messages > 0 && (
        <div className="pi-exchanged-banner">
          <span className="paragraph-sm">
            <i className="fa-solid fa-comments me-2" />
            You have already exchanged {conversation.num_messages} message
            {conversation.num_messages !== 1 ? 's' : ''} with {pitch.profile_name}.
          </span>
          {/* The inbox link only reaches conversations the viewer is part of, so for anyone
              reading someone else's request page it lands on their own inbox. Those callers
              pass onViewConversation and get the thread in this page's Inbox view instead. */}
          {onViewConversation ? (
            <button
              type="button"
              className="paragraph-sm fw-bold pi-view-conversation bg-transparent border-0 p-0"
              onClick={() => onViewConversation(pitch.id)}
            >
              View Conversation <i className="fa-solid fa-arrow-right ms-1" />
            </button>
          ) : (
            <a
              className="paragraph-sm fw-bold pi-view-conversation"
              href={`/my_inbox_v2?conversation=${conversation.conversation_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Conversation <i className="fa-solid fa-arrow-right ms-1" />
            </a>
          )}
        </div>
      )}

      <div className="pi-pitch-card-body">
        <div className="d-flex align-items-center gap-3 mb-3">
          <span className="paragraph-sm text-secondary">
            <i className="fa-regular fa-clock me-1" />
            {pitch.sent_at ? `${qformat(pitch.sent_at, "MM/dd/yy 'at' hh:mm a")} ${timeZoneAbbr(pitch.sent_at)}` : ''}
            {pitch.after_send_edited_at && <em className="ms-1">(Edited Since)</em>}
          </span>
          {location && (
            <span className="paragraph-sm text-secondary pi-meta-location" title="Sent from">
              <i className="fa-regular fa-location-dot me-1" />
              {location}
            </span>
          )}
          <span className="ms-auto d-flex align-items-center gap-2">
            {unread && <span className="pi-new-badge">NEW</span>}
            {plainText && (
              <AiDetectorButton text={plainText} pitchId={pitch.id} variant="outline-secondary" size="sm" />
            )}
          </span>
        </div>

        <div ref={clampRef} className={`message-body ${expanded ? '' : 'pi-card-text-clamped'}`}>
          {pitch.pitch_text && <MessageContent body={pitch.pitch_text} contentFormat={pitch.content_format} />}
        </div>
        {(textOverflows || expanded) && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? 'less' : '...more'}
          </button>
        )}

        {fileAttachments.map((attachment) => (
          <Attachment key={attachment.id} attachment={attachment} />
        ))}

        {(pitch.pitched_entities || []).length > 0 && (
          <div className="mt-3">
            <p className="paragraph-sm fw-bold mb-2">
              Proposed Source{pitch.pitched_entities.length > 1 ? 's' : ''}:
            </p>
            <div className="d-flex flex-wrap gap-2">
              {pitch.pitched_entities.map((entity) => (
                <div key={entity.pitched_entity_id || entity.slug} className="pi-entity-chip">
                  {entity.image_url && (
                    <ImageCropped roundedCircle src={entity.image_url} alt={entity.name} size={43} fallbackToInitials />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-baseline gap-1">
                      <span className="text-truncate">{entity.name}</span>
                      <a
                        href={`/sources/${entity.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pi-view-profile flex-shrink-0"
                      >
                        (view profile)
                      </a>
                    </div>
                    {(entity.title || entity.company_name) && (
                      <p className="text-secondary mb-0 text-truncate">
                        <EntitySubtitle entity={entity} />
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {!conversation && canReply && (
        <div className="pi-quick-replies">
          <span className="paragraph-sm text-secondary">Quick replies:</span>
          {QUICK_REPLIES.map((reply) => (
            <button
              key={reply.label}
              type="button"
              className={`pi-quick-reply ${reply.videoRequest ? 'video' : ''}`}
              onClick={() => openReply(reply)}
            >
              {reply.label}
            </button>
          ))}
        </div>
      )}

        </>
      )}

      {/* Reply modal — V1's FollowupModal anatomy, with the inbox composer in place of the textarea */}
      {muteReport && (
        <PitchMuteModal
          show={showMute}
          onHide={() => setShowMute(false)}
          durationDays={muteDuration}
          onDurationChange={(e) => setMuteDuration(e.target.value)}
          defaultMuteDurationDays={muteReport.defaultMuteDurationDays}
          onSubmit={submitMute}
        />
      )}
      {muteReport && (
        <PitchReportModal
          show={showReportAbuse}
          onHide={() => setShowReportAbuse(false)}
          abusiveBehaviorTypes={muteReport.abusiveBehaviorTypes}
          behaviorTypes={reportBehaviorTypes}
          onToggleBehaviorType={toggleBehaviorType}
          onDescriptionChange={(e) => setReportDescription(e.target.value)}
          onSubmit={submitReportAbuse}
        />
      )}

      <Modal show={showReply} onHide={() => setShowReply(false)} size="lg" centered contentClassName="pitch-inbox">
        <Modal.Header closeButton className="align-items-start">
          <div className="d-flex flex-wrap w-100 justify-content-between align-items-start gap-3 me-3">
            <Modal.Title className="paragraph-lg">Reply to {pitch.profile_name}&apos;s pitch</Modal.Title>
            <div className="d-flex gap-2">
              <ImageCropped
                roundedCircle
                src={pitch.profile_image_url}
                alt={pitch.profile_name}
                size={40}
                fallbackToInitials
              />
              <div>
                <div>
                  <a
                    href={profileUrl({ slug: pitch.profile_slug, user_migrate_id: pitch.user_migrate_id })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-bold pi-profile-link"
                  >
                    {pitch.profile_name} (see profile)
                  </a>
                </div>
                {pitch.profile_title_at_workplace && (
                  <span className="paragraph-sm text-secondary">{pitch.profile_title_at_workplace}</span>
                )}
              </div>
            </div>
          </div>
        </Modal.Header>

        <div className="border-bottom px-3 py-3">
          <div
            ref={previewRef}
            className={previewExpanded ? '' : 'pi-modal-pitch-preview'}
            style={{ whiteSpace: 'pre-line' }}
          >
            {previewText}
          </div>
          <div className="d-flex align-items-start gap-3 pt-2">
            {(previewOverflows || previewExpanded) && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none flex-shrink-0"
                onClick={() => setPreviewExpanded((e) => !e)}
              >
                {previewExpanded ? 'Collapse' : 'Expand'}
              </button>
            )}
            {(pitch.pitched_entities || []).length > 0 && (
              <div>
                <span className="badge bg-secondary mb-2">Proposed sources:</span>
                {pitch.pitched_entities.map((entity) => (
                  <div key={entity.pitched_entity_id || entity.slug} className="d-flex gap-2 pb-2">
                    {entity.image_url && (
                      <ImageCropped
                        roundedCircle
                        src={entity.image_url}
                        alt={entity.name}
                        size={32}
                        fallbackToInitials
                      />
                    )}
                    <div>
                      <a
                        href={`/sources/${entity.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-bold d-block pi-profile-link"
                      >
                        {entity.name} (see profile)
                      </a>
                      {(entity.title || entity.company_name) && (
                        <span className="paragraph-sm text-secondary">
                          <EntitySubtitle entity={entity} />
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Modal.Body>
          <div className="alert pi-reply-note">
            <i className="fa-regular fa-circle-info" /> <strong>Note:</strong> Qwoted recommends scheduling a video
            interview to validate expertise.
          </div>
          <div className="row align-items-center mb-1">
            <label htmlFor="pi-reply-to" className="col-2 col-form-label">
              To
            </label>
            <div className="col-10">
              <input id="pi-reply-to" className="form-control" value={pitch.profile_name || ''} readOnly required />
            </div>
          </div>
          <div className="row align-items-center mb-3">
            <label htmlFor="pi-reply-subject" className="col-2 col-form-label">
              Subject
            </label>
            <div className="col-10">
              <input id="pi-reply-subject" className="form-control" value={`Re: ${request.title || ''}`} disabled />
            </div>
          </div>
          <div className="my-inbox pi-reply-composer">
            <FirstSendComposer
              key={prefill ? prefill.body : 'blank'}
              pitch={pitch}
              request={request}
              conversationShape={conversationShape}
              onSent={handleSent}
              sendButtonPortal={sendSlot}
              prefill={prefill}
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top-0 pt-0">
          <div ref={setSendSlot} className="d-inline-flex" />
          <button type="button" className="btn btn-secondary" onClick={() => setShowReply(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

PitchCard.propTypes = {
  pitch: PropTypes.shape({
    id: PropTypes.number.isRequired,
    profile_name: PropTypes.string,
    profile_slug: PropTypes.string,
    profile_image_url: PropTypes.string,
    profile_workplace: PropTypes.string,
    profile_title_at_workplace: PropTypes.string,
    type_of_profile: PropTypes.string,
    pitch_text: PropTypes.string,
    content_format: PropTypes.string,
    sent_at: PropTypes.string,
    reaction: PropTypes.oneOf(['like', 'dislike']),
    user_migrate_id: PropTypes.number,
    under_audit: PropTypes.bool,
    spam_flagged: PropTypes.bool,
    after_send_edited_at: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types -- API payloads passed through verbatim
    pitch_attachments: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types -- API payloads passed through verbatim
    pitched_entities: PropTypes.array,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types -- conversations-view row passed through verbatim
  conversation: PropTypes.object,
  location: PropTypes.string,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  request: PropTypes.shape({
    slug: PropTypes.string,
    title: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  canReply: PropTypes.bool,
  onReact: PropTypes.func,
  // Set to handle "View Conversation" in-page instead of linking to the viewer's own inbox
  onViewConversation: PropTypes.func,
  muteReport: PropTypes.shape({
    reportingUserId: PropTypes.number,
    defaultMuteDurationDays: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types -- server-serialized behavior-types map
    abusiveBehaviorTypes: PropTypes.object,
  }),
  onReplied: PropTypes.func.isRequired,
};

export default PitchCard;
